import {
  CommonHelperFunctions,
  EProjectType,
  ESymbolCategory,
  IAbsolutePosition,
  IAction,
  IActionParameter,
  IComponent,
  IComponentDefinition,
  IConditionModel,
  IDisplayItem,
  IDisplayItemComponent,
  IDisplayItemProperty,
  IFixedPosition,
  IKuikaStyle,
  IProject,
  IScreenDesign,
  IScreenDesignDictionary,
  ISymbol,
  KAgentHelper,
  NewtonsoftLinqType,
  SpecialDIIds,
  classifyNewtonsoftLinqType
} from "@kuika/kuika-cl-model";
import htmlComponentsJson from "@kuika/kuika-cl-model/json/html-components.json";
import "animate.css/animate.min.css";
import {
  ConditionSetType,
  IManagedDbWhereConditionModel,
  IManagedDbWhereModel
} from "@kuika/kuika-cl-model/src/models/builder.dtos";
import { CascaderOptionType } from "antd/es/cascader";
import "react-notifications-component/dist/theme.css";
import { Guid } from "guid-typescript";
import _, { isNumber } from "lodash";
import { DefaultStyleProvider } from "../../contexts/screen-design-context/provider-partitions/default-style-provider";
import { ScreenDesignContextProvider } from "../../contexts/screen-design-context/screen-design-context-provider";
import { IDisplayItemClipboard } from "../../models/interface/display-item-clipboard";
import { IDisplayItemPasteData, IDisplayItemRenewData } from "../../models/interface/display-item-paste-data";
import { KMainFunctions } from "./main-functions";

declare let window: any;

export type ActionSymbolVisitor = (symbol: ISymbol) => void;

export class ScreenDesignHelper {
  static getSelectedDisplayItem = (
    dict: IScreenDesignDictionary,
    selectedKuikaIds: string[] | undefined
  ): IDisplayItem | undefined => {
    if (selectedKuikaIds === undefined) return;
    if (!KMainFunctions.isArrHasValue(selectedKuikaIds)) return;
    const di = dict[parseInt(selectedKuikaIds[0], 10)];
    return di;
  };

  static findComponentById = (id: string, components: IComponent[]): IComponent | undefined => {
    if (components) {
      let component = {} as IComponent | undefined;
      component = components.find((s) => s.id === id);
      if (!component) {
        const htmlComponentList: IComponent[] = htmlComponentsJson as unknown as IComponent[];
        component = htmlComponentList.find((s) => s.id === id);
      }
      return component;
    }
  };

  static createNewComponentById = (
    componentId: string,
    screenContext: ScreenDesignContextProvider
  ): IDisplayItemComponent | undefined => {
    if (screenContext) {
      const component: IComponent | undefined = ScreenDesignHelper.findComponentById(
        componentId,
        screenContext.state.project.componentSetObj.components
      );
      if (component) {
        const displayItemProperties: IDisplayItemProperty[] =
          ScreenDesignHelper.createNewDisplayItemComponentProperties(component);
        const displayItemComponent: IDisplayItemComponent = {
          id: componentId,
          properties: displayItemProperties
        };
        return displayItemComponent;
      }
    }
  };

  static convertDiplayItemComponentFromComponent(component: IComponent): IDisplayItemComponent {
    const displayItemProperties: IDisplayItemProperty[] =
      ScreenDesignHelper.createNewDisplayItemComponentProperties(component);
    return {
      id: component.id,
      properties: displayItemProperties
    };
  }

  static createNewDisplayItemComponentProperties = (component: IComponent): IDisplayItemProperty[] => {
    const propertyList: IDisplayItemProperty[] = [];
    component.properties.forEach((displayItemProperty) => {
      const property: IDisplayItemProperty = {
        name: displayItemProperty.name,
        selectedType: displayItemProperty.selectedType,
        symbol: displayItemProperty.symbol
      };
      if (displayItemProperty.isDeprecated) {
        property.isDeprecated = true;
      }
      propertyList.push(property);
    });
    return propertyList;
  };

  static createNewComponentByName = (
    componentName: string,
    project: IProject,
    isReportDesignComponentAllowed: boolean = true
  ): IDisplayItemComponent | undefined => {
    if (project) {
      const component: IComponent | undefined = CommonHelperFunctions.findComponentByName(
        componentName,
        project.componentSetObj.components,
        isReportDesignComponentAllowed
      );
      if (component) {
        const displayItemProperties: IDisplayItemProperty[] =
          ScreenDesignHelper.createNewDisplayItemComponentProperties(component);
        const displayItemComponent: IDisplayItemComponent = {
          id: component.id,
          properties: displayItemProperties,
          name: componentName,
          events: [],
          validations: []
        };
        return displayItemComponent;
      }
    }
  };

  static getComponentDefinition = (
    diComponent: IDisplayItemComponent,
    components: IComponent[]
  ): IComponentDefinition | undefined => {
    if (diComponent && components) {
      const component: IComponent | undefined = ScreenDesignHelper.findComponentById(diComponent.id, components);
      if (component) {
        const result: IComponentDefinition = {
          addChildText: component.addChildText,
          blockStyle: component.blockStyle,
          componentSetId: component.componentSetId,
          componentType: component.componentType,
          data: component.data,
          dropAreaText: component.dropAreaText,
          id: component.id,
          isItChildItem: component.isItChildItem,
          hasValidate: component.hasValidate,
          isItNavigationComponent: component.isItNavigationComponent,
          isItHiddenFromComponentList: component.isItHiddenFromComponentList,
          appSupportType: component.appSupportType,
          isDeprecated: component.isDeprecated,
          isRenamingDisabled: component.isRenamingDisabled,
          wrapWithFormItem: component.wrapWithFormItem,
          methods: component.methods,
          name: component.name,
          removeChildText: component.removeChildText,
          tagName: component.tagName,
          children: component.children,
          childrenInfo: component.childrenInfo,
          childrenNames: component.childrenNames,
          isHtml: component.isHtml,
          icon: component.icon,
          selectedChildId: component.selectedChildId,
          titleAttributeName: component.titleAttributeName,
          componentCategoryName: component.componentCategoryName,
          defaultStylingTypography: component.defaultStylingTypography,
          defaultStylingPosition: component.defaultStylingPosition,
          defaultStylingBorder: component.defaultStylingBorder,
          defaultStylingShadow: component.defaultStylingShadow,
          defaultStylingFill: component.defaultStylingFill,
          inVisibleStyles: component.inVisibleStyles,
          isReportDesignComponent: component.isReportDesignComponent,
          dragDropAreaText: component.dragDropAreaText,
          isDataRepeater: component.isDataRepeater
        };
        return result;
      }
    }
  };

  static addChildForItemContainer = (
    newComponent: IDisplayItemComponent,
    newDI: IDisplayItem,
    sd: IScreenDesign,
    screenDesignDictionary: IScreenDesignDictionary,
    components: IComponent[],
    projectType: EProjectType,
    defaultStyleProvider: DefaultStyleProvider
  ) => {
    const newComponentDefinition = ScreenDesignHelper.getComponentDefinition(newComponent, components);
    if (newComponentDefinition) {
      if (newComponentDefinition.childrenInfo && newComponentDefinition.childrenInfo.length > 0) {
        newComponentDefinition.childrenInfo.forEach((child) => {
          if (
            (child.name == "EditableTableColumn" || child.name == "TableDefaultColumn") &&
            projectType == EProjectType.Mobile
          ) {
            return;
          }
          const childObj = child;
          let childName = {} as any;
          let count = 1;
          childName = childObj;
          if (typeof childObj !== "string") {
            childName = childObj.name;
            count = childObj.count;
          }
          const childComp = CommonHelperFunctions.findComponentByName(childName, components);
          if (childComp) {
            for (let i = 0; i < count; i++) {
              let childDI = ScreenDesignHelper.createNewDisplayItemByComponent(
                sd,
                screenDesignDictionary,
                newDI.id,
                ScreenDesignHelper.convertDiplayItemComponentFromComponent(childComp),
                components
              );
              childDI.friendlyName = `${childDI.friendlyName}_${i}`;
              const newComponentDefinition: IComponentDefinition | undefined =
                ScreenDesignHelper.getComponentDefinition(childDI.item, components);
              childDI = ScreenDesignHelper.addDefaultComponentStylings(
                childDI,
                newComponentDefinition!,
                defaultStyleProvider
              );
              ScreenDesignHelper.addChildForItemContainer(
                childDI.item,
                childDI,
                sd,
                screenDesignDictionary,
                components,
                projectType,
                defaultStyleProvider
              );

              if (childDI?.item?.name === "EditableTableColumn" && childDI?.styleObj?.position) {
                childDI.styleObj.position.height = 60;
              }
              if (
                (childDI?.item?.name === "StaticTableColumn" || childDI?.item?.name === "ReportTableColumn") &&
                childDI?.styleObj?.position
              ) {
                childDI.styleObj.position.width = 200;
                childDI.styleObj.position.height = 40;
              }
              newDI.children.push(childDI);
            }
          }
        });
      } else {
        newComponentDefinition.childrenNames?.forEach((child) => {
          const childObj = child;
          let childName = {} as any;
          childName = childObj;

          const childComp = CommonHelperFunctions.findComponentByName(childName, components);
          if (childComp) {
            const childDI = ScreenDesignHelper.createNewDisplayItemByComponent(
              sd,
              screenDesignDictionary,
              newDI.id,
              ScreenDesignHelper.convertDiplayItemComponentFromComponent(childComp),
              components
            );
            ScreenDesignHelper.addChildForItemContainer(
              childDI.item,
              childDI,
              sd,
              screenDesignDictionary,
              components,
              projectType,
              defaultStyleProvider
            );
            newDI.children.push(childDI);
          }
        });
        if (newComponent.name === "KanbanBoardLaneHeader" || newComponent.name === "KanbanBoardCard") {
          const colSizePropNames = ["xs", "sm", "md", "lg", "xl", "xxl"];

          const rowComp = CommonHelperFunctions.findComponentByName("Row", components, false);
          const colComp = CommonHelperFunctions.findComponentByName("Col", components, false);
          const labelComp = CommonHelperFunctions.findComponentByName("Label", components, false);
          const iconComp = CommonHelperFunctions.findComponentByName("Icon", components, false);

          if (rowComp) {
            let rowDI = ScreenDesignHelper.createNewDisplayItemByComponent(
              sd,
              screenDesignDictionary,
              newDI.id,
              ScreenDesignHelper.convertDiplayItemComponentFromComponent(rowComp),
              components
            );

            const rowCompDef: IComponentDefinition | undefined = ScreenDesignHelper.getComponentDefinition(
              rowDI.item,
              components
            );

            rowDI = ScreenDesignHelper.addDefaultComponentStylings(rowDI, rowCompDef!, defaultStyleProvider);
            rowDI.item.events = [];
            rowDI.item.validations = [];

            rowDI.children = [];

            if (colComp) {
              let col1DI = ScreenDesignHelper.createNewDisplayItemByComponent(
                sd,
                screenDesignDictionary,
                rowDI.id,
                ScreenDesignHelper.convertDiplayItemComponentFromComponent(colComp),
                components
              );

              let col2DI = ScreenDesignHelper.createNewDisplayItemByComponent(
                sd,
                screenDesignDictionary,
                rowDI.id,
                ScreenDesignHelper.convertDiplayItemComponentFromComponent(colComp),
                components
              );

              const col1CompDef: IComponentDefinition | undefined = ScreenDesignHelper.getComponentDefinition(
                col1DI.item,
                components
              );
              const col2CompDef: IComponentDefinition | undefined = ScreenDesignHelper.getComponentDefinition(
                col2DI.item,
                components
              );

              col1DI = ScreenDesignHelper.addDefaultComponentStylings(col1DI, col1CompDef!, defaultStyleProvider);
              col2DI = ScreenDesignHelper.addDefaultComponentStylings(col2DI, col2CompDef!, defaultStyleProvider);

              col1DI.item.events = [];
              col1DI.item.validations = [];

              col2DI.item.events = [];
              col2DI.item.validations = [];

              if (col1DI.styleObj?.position) {
                col1DI.styleObj.position.textAlign = 1;
                col1DI.styleObj.position.alignItems = 2;
              }

              if (col2DI.styleObj?.position) {
                col2DI.styleObj.position.textAlign = 3;
                col2DI.styleObj.position.alignItems = 2;
              }

              col1DI.item.properties = col1DI.item.properties.map((prop) => {
                if (colSizePropNames.includes(prop.name)) {
                  prop.symbol.fixedValue = "20";
                }
                return { ...prop };
              });

              col2DI.item.properties = col2DI.item.properties.map((prop) => {
                if (colSizePropNames.includes(prop.name)) {
                  prop.symbol.fixedValue = "4";
                }
                return { ...prop };
              });

              if (labelComp && iconComp) {
                let labelDI = ScreenDesignHelper.createNewDisplayItemByComponent(
                  sd,
                  screenDesignDictionary,
                  col1DI.id,
                  ScreenDesignHelper.convertDiplayItemComponentFromComponent(labelComp),
                  components
                );

                let iconDI = ScreenDesignHelper.createNewDisplayItemByComponent(
                  sd,
                  screenDesignDictionary,
                  col2DI.id,
                  ScreenDesignHelper.convertDiplayItemComponentFromComponent(iconComp),
                  components
                );

                const labelCompDef: IComponentDefinition | undefined = ScreenDesignHelper.getComponentDefinition(
                  labelDI.item,
                  components
                );

                const iconCompDef: IComponentDefinition | undefined = ScreenDesignHelper.getComponentDefinition(
                  iconDI.item,
                  components
                );

                labelDI = ScreenDesignHelper.addDefaultComponentStylings(labelDI, labelCompDef!, defaultStyleProvider);
                iconDI = ScreenDesignHelper.addDefaultComponentStylings(iconDI, iconCompDef!, defaultStyleProvider);

                labelDI.item.events = [];
                labelDI.item.validations = [];

                iconDI.item.events = [];
                iconDI.item.validations = [];

                const labelValeuProp = labelDI.item.properties.find((x) => x.name === "value");
                if (labelValeuProp) {
                  if (newComponent.name === "KanbanBoardLaneHeader") {
                    labelValeuProp.symbol.fixedValue = "Lane Header";
                  } else {
                    labelValeuProp.symbol.fixedValue = "Card Title";
                  }
                }

                const iconProp = iconDI.item.properties.find((x) => x.name === "iconName");
                if (iconProp) iconProp.symbol.fixedValue = "delete";

                col1DI.children.push(labelDI);
                col2DI.children.push(iconDI);
              }

              rowDI.children.push(col1DI);
              rowDI.children.push(col2DI);
            }
            newDI.children.push(rowDI);
            if (newComponent.name === "KanbanBoardLaneHeader") return;

            if (rowComp) {
              let cardDescriptionRowDI = ScreenDesignHelper.createNewDisplayItemByComponent(
                sd,
                screenDesignDictionary,
                newDI.id,
                ScreenDesignHelper.convertDiplayItemComponentFromComponent(rowComp),
                components
              );

              const cardDescriptionRowCompDef: IComponentDefinition | undefined =
                ScreenDesignHelper.getComponentDefinition(cardDescriptionRowDI.item, components);

              cardDescriptionRowDI = ScreenDesignHelper.addDefaultComponentStylings(
                cardDescriptionRowDI,
                cardDescriptionRowCompDef!,
                defaultStyleProvider
              );

              cardDescriptionRowDI.children = [];
              cardDescriptionRowDI.item.events = [];
              cardDescriptionRowDI.item.validations = [];

              if (colComp) {
                let cardDescriptionColDI = ScreenDesignHelper.createNewDisplayItemByComponent(
                  sd,
                  screenDesignDictionary,
                  cardDescriptionRowDI.id,
                  ScreenDesignHelper.convertDiplayItemComponentFromComponent(colComp),
                  components
                );
                const cardDescriptionColCompDef: IComponentDefinition | undefined =
                  ScreenDesignHelper.getComponentDefinition(cardDescriptionColDI.item, components);

                cardDescriptionColDI = ScreenDesignHelper.addDefaultComponentStylings(
                  cardDescriptionColDI,
                  cardDescriptionColCompDef!,
                  defaultStyleProvider
                );

                cardDescriptionColDI.item.events = [];
                cardDescriptionColDI.item.validations = [];

                cardDescriptionColDI.item.properties = cardDescriptionColDI.item.properties.map((prop) => {
                  if (colSizePropNames.includes(prop.name)) {
                    prop.symbol.fixedValue = "24";
                  }
                  return { ...prop };
                });

                if (labelComp) {
                  let cardDescriptionLabelDI = ScreenDesignHelper.createNewDisplayItemByComponent(
                    sd,
                    screenDesignDictionary,
                    cardDescriptionColDI.id,
                    ScreenDesignHelper.convertDiplayItemComponentFromComponent(labelComp),
                    components
                  );

                  const cardDescriptionLabelCompDef: IComponentDefinition | undefined =
                    ScreenDesignHelper.getComponentDefinition(cardDescriptionLabelDI.item, components);

                  cardDescriptionLabelDI = ScreenDesignHelper.addDefaultComponentStylings(
                    cardDescriptionLabelDI,
                    cardDescriptionLabelCompDef!,
                    defaultStyleProvider
                  );

                  cardDescriptionLabelDI.item.events = [];
                  cardDescriptionLabelDI.item.validations = [];

                  const cardDescriptionLabelValueProp = cardDescriptionLabelDI.item.properties.find(
                    (x) => x.name === "value"
                  );
                  if (cardDescriptionLabelValueProp) {
                    cardDescriptionLabelValueProp.symbol.fixedValue = "Card description...";
                  }
                  cardDescriptionColDI.children.push(cardDescriptionLabelDI);
                }
                cardDescriptionRowDI.children.push(cardDescriptionColDI);
              }
              newDI.children.push(cardDescriptionRowDI);
            }
          }
        }
      }
    }
  };

  static createNewDisplayItemByComponent = (
    sd: IScreenDesign,
    screenDesignDictionary: IScreenDesignDictionary,
    parentId: number | undefined,
    component: IDisplayItemComponent,
    components: IComponent[],
    fixedPosition?: IFixedPosition,
    absolutePositionInfo?: IAbsolutePosition,
    kuikaId?: any
  ): IDisplayItem => {
    let friendlyName = "";
    const componentDefinition = ScreenDesignHelper.getComponentDefinition(component, components);
    if (componentDefinition) {
      friendlyName = KAgentHelper.createNewFriendlyName(componentDefinition.name, component.id, sd.displayItems);
      component.name = componentDefinition.name;
    }
    const newDI: IDisplayItem = {
      id: kuikaId ?? KMainFunctions.generateDisplayItemId(screenDesignDictionary),
      item: _.cloneDeep(component),
      children: [],
      parentId,
      friendlyName,
      fixedPositionInfo: fixedPosition,
      absolutePositionInfo
    };
    return newDI;
  };

  static getAllChildrenForDisplayItems = (displayItems: IDisplayItem[]): IDisplayItem[] => {
    return displayItems.flatMap((di) => ScreenDesignHelper.getAllChildrenForDI(di));
  };

  static getAllChildrenForDI = (di: IDisplayItem): IDisplayItem[] => {
    const result: IDisplayItem[] = [];

    if (!di.children?.length) return result;

    const queue: IDisplayItem[] = [...di.children];
    result.push(...di.children);

    let i: number = 0;

    while (i < queue.length) {
      const current = queue[i++];

      if (current.children?.length) {
        result.push(...current.children);
        queue.push(...current.children);
      }
    }

    return result;
  };

  static getFriendlyName = (kuikaid: string | undefined, dict: IScreenDesignDictionary): string => {
    if (!kuikaid) return "";
    const di = dict[parseInt(kuikaid, 10)];
    let friendlyName: string = "";
    if (di) {
      if (di.friendlyName) {
        friendlyName = di.friendlyName;
      }
      if (friendlyName.length === 0) {
        friendlyName = di.id.toString();
      }
    }
    if (friendlyName.length === 0) {
      friendlyName = "Component";
    }
    return friendlyName;
  };

  static findDisplayItemParentByKuikaId = (
    kuikaid: string,
    allDisplayItems: IDisplayItem[]
  ): IDisplayItem | undefined => {
    const queue: Array<{ parent: IDisplayItem | null; item: IDisplayItem }> = allDisplayItems.map((item) => ({
      parent: null,
      item
    }));

    while (queue.length > 0) {
      const { parent, item } = queue.shift()!;

      for (const child of item.children) {
        if (child.id.toString() === kuikaid) {
          return item;
        }
        queue.push({ parent: item, item: child });
      }
    }

    return undefined;
  };

  static findActionByIdInActions = (actionId: string, actions: IAction[]): IAction | undefined => {
    const queue: IAction[] = [...actions];

    while (queue.length > 0) {
      const action = queue.shift()!;

      if (action.id === actionId) {
        return action;
      }

      // Add all nested actions to queue
      if (action.ifThenElseData?.ifActions) {
        queue.push(...action.ifThenElseData.ifActions);
      }
      if (action.ifThenElseData?.elseActions) {
        queue.push(...action.ifThenElseData.elseActions);
      }
      if (action.recurringActionsData?.recurringActions) {
        queue.push(...action.recurringActionsData.recurringActions);
      }
      if (action.mqttSubscribeActionData?.mqttSubscribeActions) {
        queue.push(...action.mqttSubscribeActionData.mqttSubscribeActions);
      }
    }

    return undefined;
  };

  static findActionById = (actionId: string, di: IDisplayItem): IAction | undefined => {
    if (!actionId || !di) return undefined;

    // Use a queue for breadth-first search
    const queue: IDisplayItem[] = [di];

    while (queue.length > 0) {
      const current = queue.shift()!;

      // Check events
      if (current?.item?.events) {
        for (const event of current.item.events) {
          if (event.actions) {
            const action = this.findActionByIdInActions(actionId, event.actions);
            if (action) return action;
          }
        }
      }

      // Add children to queue
      if (current.children) {
        queue.push(...current.children);
      }
    }

    return undefined;
  };

  static findDisplayItemByFriendlyName = (friendlyName: string | undefined, DIs): IDisplayItem | undefined => {
    if (!friendlyName) return;
    const comp: IDisplayItem | undefined = KAgentHelper.findDisplayItemByFriendlyName(friendlyName, DIs);
    return comp;
  };

  static findDisplayItemByComponentName = (
    componentName: string | undefined,
    DIs: IDisplayItem[]
  ): IDisplayItem | undefined => {
    if (!componentName) return;
    return KAgentHelper.findDisplayItemByComponentName(componentName, DIs);
  };

  static preprocessScreen(screenDesign: IScreenDesign): void {
    if (!screenDesign?.displayItems) return;
    this.preprocessDisplayItems(screenDesign.displayItems);
  }

  private static preprocessDisplayItems(displayItems: IDisplayItem[]): void {
    if (!displayItems) return;

    const queue: IDisplayItem[] = [...displayItems];

    while (queue.length > 0) {
      const di = queue.shift();
      if (!di) continue;

      if (di.children && di.children.length > 0) {
        di.children.forEach((child) => {
          child.parentId = di.id;
        });
        queue.push(...di.children);
      }

      if (di.item?.properties) {
        di.item.properties.forEach((prop) => {
          if (prop.symbol?.category === ESymbolCategory.current) {
            prop.symbol.diId = di.id;
          }
        });
      }

      if (di.item?.events) {
        di.item.events.forEach((event) => {
          if (event.actions) {
            event.actions.forEach((action) => {
              if (action.parameters) {
                action.parameters.forEach((param) => {
                  if (param.symbol?.category === ESymbolCategory.current) {
                    param.symbol.diId = di.id;
                  }
                });
              }

              if (action.condition) {
                this.processConditionSymbols(action.condition, di.id);
              }

              if (action.ifThenElseData?.condition) {
                this.processConditionSymbols(action.ifThenElseData.condition, di.id);
              }

              this.preprocessAction(action);
            });
          }
        });
      }

      if (di.item.visibility?.condition) {
        this.processConditionSymbols(di.item.visibility.condition, di.id);
        this.migrateConditionSet(di.item.visibility.condition);
      }

      if (di.editability?.condition) {
        this.processConditionSymbols(di.editability.condition, di.id);
        this.migrateConditionSet(di.editability.condition);
      }

      if (di.item?.validations) {
        di.item.validations.forEach((validation) => {
          if (validation.condition) {
            this.processConditionSymbols(validation.condition, di.id);
            this.migrateConditionSet(validation.condition);
          }
        });
      }
    }
  }

  static cleanupHtmlComponentCache() {
    // No-op under ESM/bundler: html-components.json is a singleton module import.
    // The previous Node-style require.cache eviction does not apply and is not
    // needed — consumers clone/replace references rather than mutating the JSON.
  }

  private static processConditionSymbols(condition: IConditionModel, diId: number): void {
    if (!condition) return;

    if (condition.conditionSet) {
      condition.conditionSet.conditions.forEach((cond) => {
        if (cond.field1?.category === ESymbolCategory.current) {
          cond.field1.diId = diId;
        }
        if (cond.field2?.category === ESymbolCategory.current) {
          cond.field2.diId = diId;
        }
      });
    } else {
      if (condition.field1?.category === ESymbolCategory.current) {
        condition.field1.diId = diId;
      }
      if (condition.field2?.category === ESymbolCategory.current) {
        condition.field2.diId = diId;
      }
    }
  }

  private static preprocessAction(action: IAction): void {
    const queue: IAction[] = [action];

    while (queue.length > 0) {
      const currentAction = queue.shift();
      if (!currentAction) continue;

      if (currentAction.ifThenElseData) {
        if (currentAction.ifThenElseData.condition) {
          this.migrateConditionSet(currentAction.ifThenElseData.condition);
        }
        if (currentAction.ifThenElseData.ifActions) {
          queue.push(...currentAction.ifThenElseData.ifActions);
        }
        if (currentAction.ifThenElseData.elseActions) {
          queue.push(...currentAction.ifThenElseData.elseActions);
        }
      }

      if (currentAction.recurringActionsData?.recurringActions) {
        queue.push(...currentAction.recurringActionsData.recurringActions);
      }

      if (currentAction.mqttSubscribeActionData?.mqttSubscribeActions) {
        queue.push(...currentAction.mqttSubscribeActionData.mqttSubscribeActions);
      }

      if (currentAction.forEachData?.actions) {
        queue.push(...currentAction.forEachData.actions);
      }

      if (currentAction.condition) {
        this.migrateConditionSet(currentAction.condition);
      }
    }
  }

  static migrateConditionSet(condition: IConditionModel): void {
    if (!condition) return;

    if (condition.conditionSet === undefined) {
      condition.conditionSet = {
        type: ConditionSetType.and,
        conditions: []
      };

      let newCondition: IConditionModel = {
        id: Guid.create().toString(),
        field1: _.cloneDeep(condition.field1),
        conditionOperator: condition.conditionOperator,
        field2: _.cloneDeep(condition.field2)
      };

      condition.conditionSet.conditions.push(newCondition);

      for (const key in condition) {
        if (key !== "id" && key !== "conditionSet") {
          delete condition[key];
        }
      }
    } else {
      for (const key in condition) {
        if (key !== "id" && key !== "conditionSet") {
          delete condition[key];
        }
      }
    }

    this.ensureConditionIds(condition);
  }

  // A condition is addressed by its id when edited, deleted or reordered, so a set holding two
  // id-less siblings resolves all three to the first row. Ids can be absent on anything the
  // backend produced or wrote back, so they are filled in on load. This is a repair, not a user
  // edit: it must stay in memory and never mark the screen dirty.
  static ensureConditionIds(condition: IConditionModel): void {
    if (!condition) return;

    if (!condition.id) {
      condition.id = Guid.create().toString();
    }

    const children: IConditionModel[] | undefined = condition.conditionSet?.conditions;
    if (!children) return;

    children.forEach((child: IConditionModel): void => {
      this.ensureConditionIds(child);
    });
  }

  static createRootDI = (sd: IScreenDesign, screenDesignDictionary: IScreenDesignDictionary): IScreenDesign => {
    try {
      const htmlComponentList: IComponent[] = htmlComponentsJson as unknown as IComponent[];
      const divComp = htmlComponentList.find((c) => c.name.toLowerCase() === "div");
      if (divComp) {
        const iDIComponent = ScreenDesignHelper.convertDiplayItemComponentFromComponent(divComp);
        const styleProperty = iDIComponent.properties.find((x) => x.name === "style");

        if (styleProperty) {
          styleProperty.symbol = { category: ESymbolCategory.fixed } as ISymbol;
          styleProperty.symbol.fixedValue = "";
        }

        const divDI = ScreenDesignHelper.createNewDisplayItemByComponent(
          sd,
          screenDesignDictionary,
          undefined,
          iDIComponent,
          htmlComponentList
        ) as IDisplayItem;
        divDI.id = SpecialDIIds.rootDI;
        divDI.children = sd.displayItems;
        sd.displayItems = [divDI];
      }
    } finally {
      this.cleanupHtmlComponentCache();
    }
    return sd;
  };

  static fixUnreferencedNavigationComponents(displayItems: IDisplayItem[]): IDisplayItem[] {
    const actions: IAction[] = [];
    const navActions = this.findAllNavigationActions(displayItems, actions);
    const rootDI = displayItems.find((x) => x.id === SpecialDIIds.rootDI);

    if (rootDI) {
      const navDiIds = new Set(
        navActions
          .map((x) => x.parameters.find((p) => p.name === "navigationKuikaId")?.symbol.fixedValue)
          .filter(Boolean)
      );

      // Clean up unreferenced navigation components
      rootDI.children = rootDI.children.filter((x) => {
        if (x.navScreenInfo) {
          const isReferenced = navDiIds.has(x.id);
          if (!isReferenced) {
            // Clean up any resources associated with unreferenced nav components
            x.navScreenInfo = undefined;
          }
          return isReferenced;
        }
        return true;
      });
    }

    // Clear temporary arrays
    actions.length = 0;
    navActions.length = 0;

    return displayItems;
  }

  static getAllActionsUnderIfThenElseAction = (action: IAction): IAction[] => {
    const result: IAction[] = [];
    const queue: IAction[] = [action];

    while (queue.length > 0) {
      const current = queue.shift()!;

      if (current.ifThenElseData) {
        if (current.ifThenElseData.ifActions) {
          result.push(...current.ifThenElseData.ifActions);
          queue.push(...current.ifThenElseData.ifActions);
        }
        if (current.ifThenElseData.elseActions) {
          result.push(...current.ifThenElseData.elseActions);
          queue.push(...current.ifThenElseData.elseActions);
        }
      }
    }

    return result;
  };

  static findAllNavigationActions(displayItems: IDisplayItem[], actions: IAction[]): IAction[] {
    const stack: IDisplayItem[] = [...displayItems];

    while (stack.length > 0) {
      const di = stack.pop()!;

      if (di.children?.length) {
        stack.push(...di.children);
      }

      di.item.events?.forEach((e) => {
        e.actions?.forEach((act) => {
          const allIfThenElseActions = this.getAllActionsUnderIfThenElseAction(act);

          allIfThenElseActions.forEach((a) => {
            if (a.label === "NAVIGATE" || a.label === "NavigateToTask") {
              const openAsPrm = a.parameters.find((x) => x.name === "openAs");
              if (Guid.isGuid(openAsPrm?.symbol.fixedValue)) {
                actions.push(a);
              }
            }
          });

          if (act.label === "NAVIGATE" || act.label === "NavigateToTask") {
            const openAsPrm = act.parameters.find((x) => x.name === "openAs");
            if (openAsPrm?.symbol.fixedValue && Guid.isGuid(openAsPrm.symbol.fixedValue)) {
              actions.push(act);
            }
          }

          const nestedActions = this.getNestedActions(act);
          actions.push(
            ...nestedActions.filter(
              (a) =>
                a.label === "NAVIGATE" && Guid.isGuid(a.parameters.find((x) => x.name === "openAs")?.symbol.fixedValue)
            )
          );
        });
      });
    }

    return actions;
  }

  static getNestedActions(action: IAction): IAction[] {
    const nestedActions: IAction[] = [];

    if (action.ifThenElseData) {
      nestedActions.push(...(action.ifThenElseData.ifActions || []), ...(action.ifThenElseData.elseActions || []));
    }

    if (action.recurringActionsData?.recurringActions) {
      nestedActions.push(...action.recurringActionsData.recurringActions);
    }

    if (action.mqttSubscribeActionData?.mqttSubscribeActions) {
      nestedActions.push(...action.mqttSubscribeActionData.mqttSubscribeActions);
    }

    return nestedActions;
  }

  /**
   * Every symbol an action tree binds: parameters (object fields too), conditions (AND/OR groups
   * too), managed-DB save/delete bindings, and the actions nested in If/Else, ForEach, recurring
   * and MQTT-subscribe containers. `getNestedActions` lists one level of containers; this walks
   * all of them and hands out the symbols, so a rename or a stale-binding count reaches a binding
   * wherever it sits.
   */
  static forEachActionSymbol(actions: IAction[] | undefined, visit: ActionSymbolVisitor): void {
    actions?.forEach((action: IAction): void => {
      action.parameters?.forEach((parameter: IActionParameter): void => this.visitParameterSymbols(parameter, visit));
      this.visitConditionSymbols(action.condition, visit);
      this.visitConditionSymbols(action.ifThenElseData?.condition, visit);
      this.forEachActionSymbol(action.ifThenElseData?.ifActions, visit);
      this.forEachActionSymbol(action.ifThenElseData?.elseActions, visit);
      if (action.forEachData?.symbolModel) visit(action.forEachData.symbolModel);
      this.forEachActionSymbol(action.forEachData?.actions, visit);
      this.forEachActionSymbol(action.recurringActionsData?.recurringActions, visit);
      this.forEachActionSymbol(action.mqttSubscribeActionData?.mqttSubscribeActions, visit);
      action.managedDbData?.save?.parameters?.forEach((parameter: IActionParameter): void =>
        this.visitParameterSymbols(parameter, visit)
      );
      action.managedDbData?.save?.where?.forEach((where: IManagedDbWhereModel): void => {
        if (where.andCondition?.symbol) visit(where.andCondition.symbol);
        where.orConditions?.forEach((condition: IManagedDbWhereConditionModel): void => {
          if (condition.symbol) visit(condition.symbol);
        });
      });
      this.visitParameterSymbols(action.managedDbData?.delete?.idParameter, visit);
    });
  }

  private static visitConditionSymbols(condition: IConditionModel | undefined, visit: ActionSymbolVisitor): void {
    if (!condition) return;
    if (condition.field1) visit(condition.field1);
    if (condition.field2) visit(condition.field2);
    condition.conditionSet?.conditions?.forEach((child: IConditionModel): void =>
      this.visitConditionSymbols(child, visit)
    );
  }

  private static visitParameterSymbols(parameter: IActionParameter | undefined, visit: ActionSymbolVisitor): void {
    if (!parameter) return;
    if (parameter.symbol) visit(parameter.symbol);
    parameter.parameters?.forEach((child: IActionParameter): void => this.visitParameterSymbols(child, visit));
  }

  static fixStylingIds(displayItems: IDisplayItem[]): IDisplayItem[] {
    const stack: IDisplayItem[] = [...displayItems];

    while (stack.length > 0) {
      const di = stack.pop()!;

      if (di.children?.length) {
        stack.push(...di.children);
      }

      if (di.styleObj) {
        this.processStyleObj(di.styleObj);
      }
    }

    return displayItems;
  }

  private static processStyleObj(styleObj: any) {
    if (!styleObj) return;

    const styleProps = ["border", "shadow", "fill", "position", "text"];
    const idProps = ["borderId", "shadowId", "fillId", "positionId", "typoId"];

    // Process each style property
    for (let i = 0; i < styleProps.length; i++) {
      const prop = styleProps[i];
      const idProp = idProps[i];

      if (styleObj[prop]?.[idProp]?.value) {
        styleObj[prop][idProp] = styleObj[prop][idProp].value;
      }
    }
  }

  static getDefaultStylingForMobile = (
    di: IDisplayItem,
    styleType: string,
    defaultStyleProvider: DefaultStyleProvider
  ) => {
    const item = di?.item;
    const itemName = item?.name;
    const componentTag = itemName ? `#${itemName}#`.toLowerCase() : "";
    const matchesName = (name: string | undefined, target: string): boolean => {
      if (!name) return false;
      const lower = name.toLowerCase();
      return lower === target.toLowerCase() || (componentTag.length > 0 && lower.includes(componentTag));
    };
    switch (styleType) {
      case "position":
        const target = `KUIKA-${itemName}-DEFAULT-POSITION`;
        const returnPos = defaultStyleProvider
          .getMobileStyles()
          .position.filter((pos) => matchesName(pos.name, target));
        return returnPos.length > 0 ? returnPos[0].positionId : "";
      case "typo":
        const typoTarget = `KUIKA-${itemName}-DEFAULT-TYPO`;
        const returnTypo = defaultStyleProvider
          .getMobileStyles()
          .typography.filter((pos) => matchesName(pos.name, typoTarget));
        return returnTypo.length > 0 ? returnTypo[0].typoId : "";
      case "border":
        const borderTarget = `KUIKA-${itemName}-DEFAULT-BORDER`;
        const returnBorder = defaultStyleProvider
          .getMobileStyles()
          .border.filter((pos) => matchesName(pos.name, borderTarget));
        return returnBorder.length > 0 ? returnBorder[0].borderId : "";
      case "shadow":
        const shadowTarget = `KUIKA-${itemName}-DEFAULT-SHADOW`;
        const returnShadow = defaultStyleProvider
          .getMobileStyles()
          .shadow.filter((pos) => matchesName(pos.name, shadowTarget));
        return returnShadow.length > 0 ? returnShadow[0].shadowId : "";
      case "fill":
        const fillTarget = `KUIKA-${itemName}-DEFAULT-FILL`;
        const returnFill = defaultStyleProvider
          .getMobileStyles()
          .fill.filter((pos) => matchesName(pos.name, fillTarget));
        return returnFill.length > 0 ? returnFill[0].fillId : "";
      default:
        return "";
    }
  };

  static addDefaultComponentStylings = (
    di: IDisplayItem,
    compDef: IComponentDefinition,
    defaultStyleProvider: DefaultStyleProvider
  ): IDisplayItem => {
    const mobileTypo = this.getDefaultStylingForMobile(di, "typo", defaultStyleProvider);
    const mobileBorder = this.getDefaultStylingForMobile(di, "border", defaultStyleProvider);
    const mobilePosition = this.getDefaultStylingForMobile(di, "position", defaultStyleProvider);
    const mobileShadow = this.getDefaultStylingForMobile(di, "shadow", defaultStyleProvider);
    const mobileFill = this.getDefaultStylingForMobile(di, "fill", defaultStyleProvider);

    // Build pipe-separated ID from valid (non-empty, non-undefined) values.
    // Previously, undefined compDef values produced malformed IDs like "|undefined".
    const buildPipeId = (...ids: (string | undefined | null)[]): string | null => {
      const valid = ids.filter((id): id is string => id != null && id.length > 0);
      return valid.length > 0 ? valid.join("|") : null;
    };

    // Build IDs array keeping only valid entries.
    // Previously, undefined/empty values polluted the array (e.g. ["", undefined]).
    const buildIdsArray = (...ids: (string | undefined | null)[]): string[] => {
      return ids.filter((id): id is string => id != null && id.length > 0);
    };

    di.styleObj = {
      text: {
        typoId: buildPipeId(mobileTypo, compDef.defaultStylingTypography),
        typoIds: buildIdsArray(mobileTypo, compDef.defaultStylingTypography)
      },
      border: {
        borderId: buildPipeId(mobileBorder, compDef.defaultStylingBorder),
        borderIds: buildIdsArray(mobileBorder, compDef.defaultStylingBorder)
      },
      position: {
        positionId: buildPipeId(mobilePosition, compDef.defaultStylingPosition),
        positionIds: buildIdsArray(mobilePosition, compDef.defaultStylingPosition)
      },
      shadow: {
        shadowId: buildPipeId(mobileShadow, compDef.defaultStylingShadow),
        shadowIds: buildIdsArray(mobileShadow, compDef.defaultStylingShadow)
      },
      fill: {
        fillId: buildPipeId(mobileFill, compDef.defaultStylingFill),
        fillIds: buildIdsArray(mobileFill, compDef.defaultStylingFill)
      }
    } as IKuikaStyle;

    return di;
  };

  static getRootDiIdxInDisplayItems = (displayItems: IDisplayItem[]): number => {
    const rootDI = displayItems.find((x) => x.id === SpecialDIIds.rootDI);
    let rootDiIdx = -1;
    if (rootDI) {
      rootDiIdx = displayItems.indexOf(rootDI);
    }

    return rootDiIdx;
  };

  static isItemRelatedWithHeaderOrFooter = (kuikaId: string, dict: IScreenDesignDictionary): boolean => {
    if (
      kuikaId === SpecialDIIds.headerDI.toString() ||
      kuikaId === SpecialDIIds.footerDI.toString() ||
      kuikaId === SpecialDIIds.pageHeaderDI.toString() ||
      kuikaId === SpecialDIIds.pageFooterDI.toString()
    ) {
      return true;
    }
    const curDI = dict[parseInt(kuikaId, 10)];
    if (curDI && curDI.parentId) {
      return ScreenDesignHelper.isItemRelatedWithHeaderOrFooter(curDI.parentId.toString(), dict);
    }
    return false;
  };

  static isDiFriendlyNameUnique = (
    displayItems: IDisplayItem[],
    kuikaId: string,
    newName: string,
    result: boolean = true
  ): boolean => {
    if (result === false) return result;
    displayItems.forEach((di) => {
      if (di.friendlyName === newName && di.id.toString() !== kuikaId) result = false;
      if (di.children.length > 0)
        result = ScreenDesignHelper.isDiFriendlyNameUnique(di.children, kuikaId, newName, result);
    });
    return result;
  };

  static incrementFriendlyName = (friendlyName: string): string => {
    const match = friendlyName.match(/\d+$/);
    if (match) {
      const number = parseInt(match[0], 10) + 1;
      return friendlyName.replace(/\d+$/, number.toString());
    }
    return `${friendlyName} 1`;
  };

  static generateUniqueDisplayItemId = (
    screenDesignDictionary: IScreenDesignDictionary,
    attempts: number = 0
  ): number => {
    if (attempts > 100) {
      throw new Error("Unable to generate unique ID after 100 attempts");
    }

    const newId = KMainFunctions.generateDisplayItemId(screenDesignDictionary);
    if (screenDesignDictionary[newId]) {
      return this.generateUniqueDisplayItemId(screenDesignDictionary, attempts + 1);
    }

    return newId;
  };

  static updateSymbolReferences = (di: IDisplayItem, oldId: number, newId: number): void => {
    if (di.item?.properties) {
      di.item.properties.forEach((prop) => {
        if (prop.symbol?.diId === oldId) {
          prop.symbol.diId = newId;
        }
      });
    }
  };

  static generateUniqueFriendlyName = (baseName: string, existingNames: Set<string>): string => {
    let newName = baseName;

    // If the base name already ends with a number
    const match = baseName.match(/^(.*?)(\d+)?$/);
    if (!match) return baseName;

    const nameWithoutNumber = match[1];
    let counter = match[2] ? parseInt(match[2], 10) : 0;

    // If the base name doesn't exist, and it doesn't end with a number,
    // we can use it as is
    if (!existingNames.has(baseName) && !match[2]) {
      existingNames.add(baseName);
      return baseName;
    }

    // Find the next available number
    while (existingNames.has(newName)) {
      counter++;
      newName = `${nameWithoutNumber}${counter}`;
    }

    existingNames.add(newName);
    return newName;
  };

  static collectExistingFriendlyNames = (
    displayItems: IDisplayItem[],
    existingNames: Set<string> = new Set()
  ): Set<string> => {
    const queue: IDisplayItem[] = [...displayItems];

    while (queue.length > 0) {
      const di = queue.shift();
      if (!di) continue;

      if (di.friendlyName) {
        existingNames.add(di.friendlyName);
      }

      if (di.children?.length) {
        queue.push(...di.children);
      }
    }

    return existingNames;
  };

  static validateFriendlyNames = (di: IDisplayItem): Set<string> => {
    const names = new Set<string>();
    const queue: IDisplayItem[] = [di];
    const duplicates = new Set<string>();

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) continue;

      if (!current.friendlyName) {
        throw new Error(`Missing friendlyName for item with id ${current.id}`);
      }

      if (names.has(current.friendlyName)) {
        duplicates.add(current.friendlyName);
      }
      names.add(current.friendlyName);

      if (current.children?.length) {
        queue.push(...current.children);
      }
    }

    if (duplicates.size > 0) {
      throw new Error(`Duplicate friendly names found: ${Array.from(duplicates).join(", ")}`);
    }

    return names;
  };

  static renewDisplayItem = (
    displayItem: IDisplayItem,
    screenDesignDictionary: IScreenDesignDictionary,
    existingNames?: Set<string>
  ): IDisplayItem => {
    const queue: { oldDi: IDisplayItem; newDi: IDisplayItem }[] = [];
    const friendlyNames = existingNames || new Set<string>();

    if (!existingNames) {
      Object.values(screenDesignDictionary).forEach((di) => {
        this.collectExistingFriendlyNames([di], friendlyNames);
      });
    }

    const rootDi: IDisplayItem = {
      ...displayItem,
      item: {
        ...displayItem.item,
        events: [],
        properties: _.cloneDeep(displayItem.item.properties)
      },
      children: [],
      id: this.generateUniqueDisplayItemId(screenDesignDictionary),
      friendlyName: this.generateUniqueFriendlyName(
        displayItem.friendlyName || displayItem.item.name || "Component",
        friendlyNames
      )
    };

    if (displayItem.children?.length) {
      queue.push({ oldDi: displayItem, newDi: rootDi });
    }

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) continue;

      const { oldDi, newDi } = current;

      oldDi.children?.forEach((childDi) => {
        const newChildDi: IDisplayItem = {
          ...childDi,
          item: {
            ...childDi.item,
            events: [],
            properties: _.cloneDeep(childDi.item.properties)
          },
          children: [],
          parentId: newDi.id,
          id: this.generateUniqueDisplayItemId(screenDesignDictionary),
          friendlyName: this.generateUniqueFriendlyName(
            childDi.friendlyName || childDi.item.name || "Component",
            friendlyNames
          )
        };

        this.updateSymbolReferences(newChildDi, childDi.id, newChildDi.id);

        newDi.children.push(newChildDi);

        if (childDi.children?.length) {
          queue.push({ oldDi: childDi, newDi: newChildDi });
        }
      });
    }

    this.validateFriendlyNames(rootDi);

    return rootDi;
  };

  static renewDisplayItems = (
    displayItems: IDisplayItem[],
    newParentId: number,
    screenDesignDictionary: IScreenDesignDictionary
  ): IDisplayItem[] => {
    if (!displayItems?.length) return displayItems;

    const result: IDisplayItem[] = [];
    const friendlyNames = new Set<string>();

    Object.values(screenDesignDictionary).forEach((di) => {
      this.collectExistingFriendlyNames([di], friendlyNames);
    });

    const queue: { items: IDisplayItem[]; parentId: number; resultArray: IDisplayItem[] }[] = [
      { items: displayItems, parentId: newParentId, resultArray: result }
    ];

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) continue;

      const { items, parentId, resultArray } = current;

      items.forEach((di) => {
        const newDi: IDisplayItem = {
          ...di,
          item: {
            ...di.item,
            events: [],
            properties: _.cloneDeep(di.item.properties)
          },
          children: [],
          parentId,
          id: this.generateUniqueDisplayItemId(screenDesignDictionary),
          friendlyName: this.generateUniqueFriendlyName(di.friendlyName || di.item.name || "Component", friendlyNames)
        };

        this.updateSymbolReferences(newDi, di.id, newDi.id);

        resultArray.push(newDi);

        if (di.children?.length) {
          queue.push({
            items: di.children,
            parentId: newDi.id,
            resultArray: newDi.children
          });
        }
      });
    }

    result.forEach((di) => this.validateFriendlyNames(di));

    return result;
  };

  static findRelatedNavigationDisplayItems = (
    di: IDisplayItem,
    dict: IScreenDesignDictionary,
    relatedNavigationDisplayItems: IDisplayItem[]
  ) => {
    const { item } = di;
    item?.events?.forEach((event) => {
      event?.actions?.forEach((action) => {
        if (action.label === "NAVIGATE" || action.label === "NavigateToTask") {
          const navKuikaIdParam = action.parameters.find((x) => x.name === "navigationKuikaId");
          if (navKuikaIdParam && navKuikaIdParam?.symbol?.fixedValue) {
            const navDI = dict[parseInt(navKuikaIdParam.symbol.fixedValue, 10)];
            if (navDI) {
              relatedNavigationDisplayItems.push(navDI);
            }
          }
        }
      });
    });

    if (!di.children || di.children.length === 0) return;

    di.children.forEach((x) => {
      this.findRelatedNavigationDisplayItems(x, dict, relatedNavigationDisplayItems);
    });
  };

  static prepareDisplayItemPasteData = (
    clipboardValue: IDisplayItemClipboard,
    sd: IScreenDesign,
    screenDesignDictionary: IScreenDesignDictionary
  ): IDisplayItemPasteData => {
    const returnData = {} as IDisplayItemPasteData;
    const friendlyNames = new Set<string>();

    Object.values(screenDesignDictionary).forEach((di) => {
      this.collectExistingFriendlyNames([di], friendlyNames);
    });
    this.collectExistingFriendlyNames(sd.displayItems, friendlyNames);

    let di = clipboardValue.displayItem;
    const oldId: number = di.id;
    const newId: number = this.generateUniqueDisplayItemId(screenDesignDictionary);
    const navComps = clipboardValue.relatedNavigationComponents;

    const diRenewData = {
      actionIdsToReplace: [],
      diIdsToReplace: [],
      navDiIdsToReplace: [],
      screenDesignDictionary
    } as IDisplayItemRenewData;

    this.prepareDiRenewData(di, diRenewData);
    this.handleDiRenewData(di, diRenewData, newId);

    let diJson: string = JSON.stringify(di);
    diJson = ScreenDesignHelper.replaceAnIdInSerializedDi(diJson, oldId, newId);
    di = JSON.parse(diJson);

    navComps.forEach((navComp) => {
      navComp.id = this.findNewId(navComp.id, diRenewData, "navDiId");
      navComp.parentId = SpecialDIIds.rootDI;
    });

    di.friendlyName = this.generateUniqueFriendlyName(di.friendlyName || di.item.name || "Component", friendlyNames);

    if (di && di.fixedPositionInfo && window.kuika?.isAbsoluteDesign === true) {
      const fixedPositionInfo = _.cloneDeep(di.fixedPositionInfo);
      if (fixedPositionInfo.left) {
        fixedPositionInfo.left += 20;
      }
      if (fixedPositionInfo.top) {
        fixedPositionInfo.top += 20;
      }
      di.fixedPositionInfo = fixedPositionInfo;
      di.absolutePositionInfo = _.cloneDeep(di.absolutePositionInfo);
    }

    if (di.item.events) {
      di.item.events = [];
    }

    if (di.children?.length) {
      const queue: { item: IDisplayItem; parentId: number }[] = di.children.map((child) => ({
        item: child,
        parentId: di.id
      }));

      while (queue.length > 0) {
        const current = queue.shift();
        if (!current) continue;

        const { item, parentId } = current;

        item.parentId = parentId;
        const oldChildId = item.id;
        item.id = this.generateUniqueDisplayItemId(screenDesignDictionary);

        this.updateSymbolReferences(item, oldChildId, item.id);

        item.friendlyName = this.generateUniqueFriendlyName(
          item.friendlyName || item.item.name || "Component",
          friendlyNames
        );

        if (item.item.events) {
          item.item.events = [];
        }

        if (item.children?.length) {
          queue.push(
            ...item.children.map((child) => ({
              item: child,
              parentId: item.id
            }))
          );
        }
      }
    }

    this.validateFriendlyNames(di);

    returnData.displayItem = di;
    returnData.relatedNavigationComponents = navComps;
    return returnData;
  };

  private static prepareDiRenewData = (di: IDisplayItem, diRenewData: IDisplayItemRenewData) => {
    const queue: IDisplayItem[] = [di];

    while (queue.length > 0) {
      const current = queue.shift()!;

      diRenewData.diIdsToReplace.push({
        oldId: current.id,
        newId: KMainFunctions.generateDisplayItemId(diRenewData.screenDesignDictionary)
      });

      // Process events and actions
      current.item?.events?.forEach((e) => {
        e.actions.forEach((act) => {
          diRenewData.actionIdsToReplace.push({
            oldId: act.id,
            newId: Guid.create().toString()
          });

          if (act.label === "NAVIGATE" || act.label === "NavigateToTask") {
            const navKuikaIdParam = act.parameters.find((x) => x.name === "navigationKuikaId");
            const val = navKuikaIdParam?.symbol?.fixedValue;
            if (val && isNumber(val)) {
              diRenewData.navDiIdsToReplace.push({
                oldId: val,
                newId: KMainFunctions.generateDisplayItemId(diRenewData.screenDesignDictionary)
              });
            }
          }
        });
      });

      if (current.children?.length) {
        queue.push(...current.children);
      }
    }
  };

  private static handleDiRenewData = (di: IDisplayItem, diRenewData: IDisplayItemRenewData, newId?: number) => {
    const queue: IDisplayItem[] = [di];

    while (queue.length > 0) {
      const current = queue.shift()!;

      current.id = newId ?? this.findNewId(current.id, diRenewData, "diId");
      current.parentId = this.findNewId(current.parentId, diRenewData, "diId");

      current.item?.events?.forEach((e) => {
        e?.actions?.forEach((act) => {
          act.id = this.findNewId(act.id, diRenewData, "actionId");

          if (act.label === "NAVIGATE" || act.label === "NavigateToTask") {
            const navKuikaIdParam = act?.parameters?.find((x) => x.name === "navigationKuikaId");
            const val = navKuikaIdParam?.symbol?.fixedValue;
            if (navKuikaIdParam && val && isNumber(val)) {
              navKuikaIdParam.symbol.fixedValue = this.findNewId(
                navKuikaIdParam.symbol.fixedValue,
                diRenewData,
                "navDiId"
              );
            }
          }

          act.parameters.forEach((p) => {
            if (p.symbol.diId) {
              p.symbol.diId = this.findNewId(p.symbol.diId, diRenewData, "diId");
            }
            if (p.symbol.actionDefinitionId) {
              p.symbol.actionDefinitionId = this.findNewId(p.symbol.actionDefinitionId, diRenewData, "actionId");
            }
          });
        });
      });

      if (current.children?.length) {
        queue.push(...current.children);
      }
    }
  };

  private static findNewId = (
    id: number | string | undefined,
    diRenewData: IDisplayItemRenewData,
    mode: "diId" | "actionId" | "navDiId"
  ) => {
    let newIdObj;
    switch (mode) {
      case "diId":
        newIdObj = diRenewData.diIdsToReplace.find((x) => x.oldId === id);
        if (newIdObj) return newIdObj.newId;
        break;

      case "actionId":
        newIdObj = diRenewData.actionIdsToReplace.find((x) => x.oldId === id);
        if (newIdObj) return newIdObj.newId;
        break;

      case "navDiId":
        newIdObj = diRenewData.navDiIdsToReplace.find((x) => x.oldId === id);
        if (newIdObj) return newIdObj.newId;
        break;
    }

    return id;
  };

  // True when a component with the given name already exists anywhere in the tree.
  // Used to enforce single-instance components (MicroApp, the agent chat FAB).
  static componentExistsByName = (displayItems: IDisplayItem[] | undefined, name: string): boolean => {
    if (!displayItems?.length) return false;

    const queue: IDisplayItem[] = [...displayItems];

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (current.item.name === name) return true;
      if (current.children?.length) queue.push(...current.children);
    }

    return false;
  };

  static isMicroAppExist = (displayItems: IDisplayItem[] | undefined): boolean =>
    ScreenDesignHelper.componentExistsByName(displayItems, "MicroApp");

  static isParentHeaderOrFooter = (di: IDisplayItem | undefined, dict: IScreenDesignDictionary): boolean => {
    if (!di?.parentId) return false;

    let current = di;
    while (current) {
      if (
        current.item.name === "LayoutHeader" ||
        current.item.name === "LayoutFooter" ||
        current.item.name === "Header" ||
        current.item.name === "Footer" ||
        current.item.name === "Page Header" ||
        current.item.name === "Page Footer"
      ) {
        return true;
      }
      if (!current.parentId) return false;
      current = dict[current.parentId];
    }
    return false;
  };

  static isParentLeftOrRightLayout = (di: IDisplayItem | undefined, dict: IScreenDesignDictionary): boolean => {
    if (!di?.parentId) return false;

    let current = di;
    while (current) {
      if (current.item.name === "LayoutLeft" || current.item.name === "LayoutRight") {
        return true;
      }
      if (!current.parentId) return false;
      current = dict[current.parentId];
    }
    return false;
  };

  static getIsDiExistingInDisplayItems = (diId: number, displayItems: IDisplayItem[]): boolean => {
    const queue: IDisplayItem[] = [...displayItems];

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (current.id === diId) return true;
      if (current.children?.length) queue.push(...current.children);
    }
    return false;
  };

  static findParentTable = (di: IDisplayItem, dict: IScreenDesignDictionary): IDisplayItem | undefined => {
    if (!di.parentId) return undefined;

    let current = dict[di.parentId];
    while (current) {
      if (current.item.name === "Table" || current.item.name === "ReportTable" || current.item.name === "StaticTable") {
        return current;
      }

      if (!current.parentId) return undefined;

      current = dict[current.parentId];
    }
    return undefined;
  };

  static getAnyChildsInteractionDisabled = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    const di = dict[kuikaId];
    if (!di) return false;

    const queue: IDisplayItem[] = [di];
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (current.isInteractionDisabled) return true;
      if (current.children?.length) queue.push(...current.children);
    }
    return false;
  };

  static getFriendlyNamesOfChildDisplayItemsWhichInteractionsDisabled = (
    kuikaId: number,
    dict: IScreenDesignDictionary
  ): string[] => {
    const di = dict[kuikaId];
    if (!di) return [];

    const result: string[] = [];
    const queue: IDisplayItem[] = [di];

    while (queue.length > 0) {
      const current = queue.shift()!;

      if (current.isInteractionDisabled && current.friendlyName) {
        result.push(current.friendlyName);
      }

      if (current.children?.length) {
        queue.push(...current.children);
      }
    }

    return result;
  };

  static replaceAnIdInSerializedDi = (serializedDi: string, oldId: number, newId: number): string => {
    return serializedDi
      .replace(`"id":${oldId}`, `"id":${newId}`)
      .replace(`"parentId":${oldId}`, `"parentId":${newId}`)
      .replace(`"diId":${oldId}`, `"diId":${newId}`)
      .replace(`[diId:${oldId}]`, `[diId:${newId}]`)
      .replace(`"fixedValue":${oldId}`, `"fixedValue":${newId}`)
      .replace(`"fixedValue":"${oldId}"`, `"fixedValue":"${newId}"`);
  };

  static fetchPredecessorForEachActionsOfAction = (actionId: string, listOfActions: IAction[]): IAction[] => {
    const queue: Array<{ actions: IAction[]; path: IAction[] }> = [{ actions: listOfActions, path: [] }];

    while (queue.length > 0) {
      const { actions, path } = queue.shift()!;

      for (const action of actions) {
        if (action.id === actionId) {
          return path;
        }

        const ifThenElseActions: IAction[] = action.ifThenElseData?.ifActions ?? [];
        const elseActions: IAction[] = action.ifThenElseData?.elseActions ?? [];
        const forEachActions: IAction[] = action.forEachData?.actions ?? [];
        const mqttActions: IAction[] = action.mqttSubscribeActionData?.mqttSubscribeActions ?? [];
        const recurringActions: IAction[] = action.recurringActionsData?.recurringActions ?? [];

        queue.push({
          actions: [...ifThenElseActions, ...elseActions, ...forEachActions, ...mqttActions, ...recurringActions],
          path: [...path, action]
        });
      }
    }

    return [];
  };

  static getIsInteractionDisabled = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    const di = dict[kuikaId];

    if (!di) {
      return false;
    }

    if (di.isInteractionDisabled) {
      return true;
    }

    return false;
  };

  static getIsParentsChildrenInteractionDisabled = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    return CommonHelperFunctions.getIsParentsChildrenInteractionDisabled(kuikaId, dict);
  };

  static getIsChildOfDataRepeater = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    return CommonHelperFunctions.getIsChildOfDataRepeater(kuikaId, dict);
  };

  static getIsChildOfForm = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    return CommonHelperFunctions.getIsChildOfForm(kuikaId, dict);
  };

  static getAllSymbolsInsideDisplayItem(di: IDisplayItem): ISymbol[] {
    if (!di) return [];

    const symbols: ISymbol[] = [];
    const queue: IDisplayItem[] = [di];

    while (queue.length > 0) {
      const currentDi = queue.shift();
      if (!currentDi) continue;

      if (currentDi.children && currentDi.children.length > 0) {
        queue.push(...currentDi.children);
      }

      if (currentDi.item?.properties) {
        currentDi.item.properties.forEach((prop) => {
          if (prop.symbol) {
            symbols.push(prop.symbol);
          }
        });
      }

      if (currentDi.item?.events) {
        currentDi.item.events.forEach((event) => {
          if (event.actions) {
            event.actions.forEach((action) => {
              if (action.parameters) {
                action.parameters.forEach((param) => {
                  if (param.symbol) {
                    symbols.push(param.symbol);
                  }
                });
              }

              if (action.condition) {
                this.collectSymbolsFromCondition(action.condition, symbols);
              }

              if (action.ifThenElseData?.condition) {
                this.collectSymbolsFromCondition(action.ifThenElseData.condition, symbols);
              }
            });
          }
        });
      }

      if (currentDi.item?.validations) {
        currentDi.item.validations.forEach((validation) => {
          if (validation.condition) {
            this.collectSymbolsFromCondition(validation.condition, symbols);
          }
        });
      }

      if (currentDi.editability?.condition) {
        this.collectSymbolsFromCondition(currentDi.editability.condition, symbols);
      }
    }

    return symbols;
  }

  private static collectSymbolsFromCondition(condition: IConditionModel, symbols: ISymbol[]): void {
    if (!condition) return;

    if (condition.conditionSet) {
      condition.conditionSet.conditions.forEach((cond) => {
        if (cond.field1) {
          symbols.push(cond.field1);
        }
        if (cond.field2) {
          symbols.push(cond.field2);
        }
      });
    } else {
      if (condition.field1) {
        symbols.push(condition.field1);
      }
      if (condition.field2) {
        symbols.push(condition.field2);
      }
    }
  }

  private static toShortLowerTypeName = (typeName: string): string => {
    if (!typeName) return typeName;
    const lastDot: number = typeName.lastIndexOf(".");
    const shortName: string = lastDot < 0 ? typeName : typeName.substring(lastDot + 1);
    return shortName.toLowerCase();
  };

  public static generateTypeConversion = (sourceTypename: string, targetTypeName: string) => {
    const result = [] as CascaderOptionType[];

    // Conversion items are keyed by the SHORT lower-case type name; a datasource field arrives
    // namespace-qualified (e.g. Newtonsoft.Json.Linq.JObject), as getAreTypesCompatible already allows.
    sourceTypename = ScreenDesignHelper.toShortLowerTypeName(sourceTypename);
    targetTypeName = ScreenDesignHelper.toShortLowerTypeName(targetTypeName);

    if (!this.getAreTypesCompatible(sourceTypename, targetTypeName)) {
      const conversionActions = this.fillConversionItems().children;
      const tmp = conversionActions!
        .filter((x) => x.fromTypes?.includes(sourceTypename))
        .filter((x) => x.toTypes?.includes(targetTypeName));
      for (let i = 0; i < tmp.length; i++) {
        tmp[i].category = ESymbolCategory.typeConvertion;
        result.push(tmp[i]);
      }
    }
    return result;
  };

  public static fillConversionItems = (): CascaderOptionType => {
    return {
      category: ESymbolCategory.typeConvertions,
      label: "Conversion",
      children: [
        {
          category: ESymbolCategory.typeConvertion,
          typeConversion: "dateGetDay",
          label: "Day of Date",
          fromTypes: ["datetimeoffset", "datetime"],
          toTypes: ["decimal", "int32", "number", "int64", "int16"]
        },
        {
          category: ESymbolCategory.typeConvertion,
          typeConversion: "dateGetMonth",
          label: "Month of Date",
          fromTypes: ["datetimeoffset", "datetime"],
          toTypes: ["decimal", "int32", "number", "int64", "int16"]
        },
        {
          category: ESymbolCategory.typeConvertion,
          typeConversion: "dateGetYear",
          label: "Year of Date",
          fromTypes: ["datetimeoffset", "datetime"],
          toTypes: ["decimal", "int32", "number", "int64", "int16"]
        },
        {
          category: ESymbolCategory.typeConvertion,
          typeConversion: "dateIsWeekDay",
          label: "Is Week Day",
          fromTypes: ["datetimeoffset", "datetime"],
          toTypes: ["boolean"]
        },
        {
          category: ESymbolCategory.typeConvertion,
          typeConversion: "toDate",
          label: "To Date",
          fromTypes: ["string"],
          toTypes: ["datetimeoffset", "datetime"]
        },
        {
          category: ESymbolCategory.typeConvertion,
          typeConversion: "toInteger",
          label: "To Integer",
          fromTypes: ["boolean", "string", "int64", "decimal", "double"],
          toTypes: ["int32", "number", "int64", "int16"]
        },
        {
          category: ESymbolCategory.typeConvertion,
          typeConversion: "toDecimal",
          label: "To Decimal",
          fromTypes: ["boolean", "string", "int64", "int32", "int16"],
          toTypes: ["decimal", "number"]
        },
        {
          category: ESymbolCategory.typeConvertion,
          typeConversion: "toDouble",
          label: "To Double",
          fromTypes: ["boolean", "string", "int64", "int32", "int16"],
          toTypes: ["double", "number"]
        },
        {
          category: ESymbolCategory.typeConvertion,
          typeConversion: "toString",
          label: "To String",
          fromTypes: [
            "guid",
            "boolean",
            "datetimeoffset",
            "datetime",
            "date",
            "int32",
            "decimal",
            "double",
            "number",
            "int64",
            "float",
            "int16",
            // KOA-18737: a raw-JSON JObject / JArray member is only ever shown as text.
            "jobject",
            "jarray"
          ],
          toTypes: ["string"]
        },
        {
          category: ESymbolCategory.typeConvertion,
          typeConversion: "toBoolean",
          label: "To Boolean",
          fromTypes: ["string", "int32", "decimal", "double", "number", "int64", "int16"],
          toTypes: ["boolean"]
        },
        {
          category: ESymbolCategory.typeConvertion,
          typeConversion: "toGuid",
          label: "To Guid",
          fromTypes: ["string"],
          toTypes: ["guid"]
        }
      ]
    } as CascaderOptionType;
  };

  public static getAreTypesCompatible = (sourceTypename: string, targetTypeName: string): boolean => {
    if (!sourceTypename) sourceTypename = "string";
    if (targetTypeName === "Unknown" || sourceTypename === "Unknown") return true;
    if (targetTypeName === "Any" || sourceTypename === "Any") return true;

    if (sourceTypename.includes(".") && sourceTypename.length > 0) {
      const sourceLast = sourceTypename.split(".").pop();
      if (sourceLast) sourceTypename = sourceLast.toLowerCase();
    }
    if (targetTypeName.includes(".") && targetTypeName.length > 0) {
      const targetLast = targetTypeName.split(".").pop();
      if (targetLast) targetTypeName = targetLast.toLowerCase();
    }
    sourceTypename = sourceTypename.toLowerCase();
    targetTypeName = targetTypeName.toLowerCase();

    // KOA-18737: raw-JSON datasource types. A JToken may hold any JSON value, so it binds anywhere; a
    // JObject / JArray is an object / array value and binds where an object (or the same kind) is accepted.
    // Without this the field never showed up in the picker, not even to pass it on to another action.
    const sourceRawJson: NewtonsoftLinqType | null = classifyNewtonsoftLinqType(sourceTypename);
    const targetRawJson: NewtonsoftLinqType | null = classifyNewtonsoftLinqType(targetTypeName);
    if (sourceRawJson === "JToken" || targetRawJson === "JToken") return true;
    const jsonHosts: string[] = ["object", "any", "unknown"];
    if (sourceRawJson !== null && (jsonHosts.includes(targetTypeName) || targetRawJson === sourceRawJson)) return true;
    // A string may carry JSON text: every runtime parses it into the JObject / JArray parameter
    // (Kotlin toJsonObject, Swift toKJsonObject, the generated backend's converter on the web).
    if (targetRawJson !== null && (jsonHosts.includes(sourceTypename) || sourceTypename === "string")) return true;

    const numerics = ["number", "decimal", "int32", "double", "int64", "float", "int16"];
    const files = ["string", "filecontent"];
    const images = ["string", "image"];
    const locations = ["string", "location"];
    const dates = ["datetime", "datetimeoffset", "date", "string"];
    const ids = ["guid"];
    const colors = ["colorpicker", "string"];
    const formCollections = ["iformcollection"];

    if (numerics.includes(sourceTypename) && numerics.includes(targetTypeName)) return true;
    if (locations.includes(sourceTypename) && locations.includes(targetTypeName)) return true;
    if (files.includes(sourceTypename) && files.includes(targetTypeName)) return true;
    if (images.includes(sourceTypename) && images.includes(targetTypeName)) return true;
    if (dates.includes(sourceTypename) && dates.includes(targetTypeName)) return true;
    if (ids.includes(sourceTypename) && ids.includes(targetTypeName)) return true;
    if (colors.includes(sourceTypename) && colors.includes(targetTypeName)) return true;
    if (formCollections.includes(sourceTypename) && formCollections.includes(targetTypeName)) return true;

    if (
      !numerics.includes(sourceTypename) &&
      !locations.includes(sourceTypename) &&
      !files.includes(sourceTypename) &&
      !images.includes(sourceTypename) &&
      !dates.includes(sourceTypename) &&
      !ids.includes(sourceTypename) &&
      sourceTypename !== "boolean" &&
      targetTypeName === "array"
    )
      return true;

    return sourceTypename === targetTypeName;
  };
}
